import { Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { NotificationListItem } from '@/types/notifications';
import { formatRelativeTime, notificationDisplay } from './notificationDisplay';

interface NotificationItemProps {
  item: NotificationListItem;
  /** Navigate to the underlying workflow. Omit to make the row non-clickable. */
  onSelect?: (item: NotificationListItem) => void;
  /** Resolve handler for open items. Omit on already-resolved (history) rows. */
  onResolve?: (id: string) => void;
  isResolving?: boolean;
}

/**
 * Presentational row for a single notification. Pure (no data hooks) so it's easy
 * to test and reuse in the center popover, the per-referral panel, and the
 * full-history page. Open rows show a resolve action and an unread accent;
 * resolved rows render muted with a "Resolved" marker and no action.
 */
export function NotificationItem({
  item,
  onSelect,
  onResolve,
  isResolving,
}: NotificationItemProps) {
  const {
    icon: Icon,
    title,
    subtitle,
  } = notificationDisplay(item.type, item.data);
  const isResolved = item.resolvedAt !== null;
  const isUnread = !isResolved && !item.seen;

  return (
    <div
      className={cn(
        'flex items-start gap-3 border-l-2 px-4 py-3',
        isUnread ? 'border-primary bg-secondary/40' : 'border-transparent'
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          isResolved ? 'text-body-base' : 'text-long-secondary'
        )}
        aria-hidden
      />
      <button
        type="button"
        onClick={onSelect ? () => onSelect(item) : undefined}
        disabled={!onSelect}
        className="min-w-0 flex-1 text-left disabled:cursor-default"
      >
        <span className="flex items-center gap-2">
          {isUnread && (
            <span
              className="h-2 w-2 shrink-0 rounded-full bg-primary"
              aria-hidden
            />
          )}
          <span
            className={cn(
              'truncate',
              isResolved
                ? 'text-body-small-normal text-long-secondary'
                : 'text-body-small-strong'
            )}
          >
            {title}
          </span>
        </span>
        {subtitle && (
          <span className="text-caption-long-secondary block truncate">
            {subtitle}
          </span>
        )}
        <span className="text-caption-long-secondary block">
          {formatRelativeTime(item.createdAt)}
        </span>
      </button>
      {isResolved ? (
        <span className="text-caption-long-secondary inline-flex shrink-0 items-center gap-1 self-center">
          <Check className="h-4 w-4 text-primary" aria-hidden />
          Resolved
        </span>
      ) : onResolve ? (
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 self-center gap-1.5 text-primary hover:bg-secondary"
          onClick={() => onResolve(item.id)}
          disabled={isResolving}
          aria-label={`Resolve: ${title}`}
          title="Resolve this notification"
        >
          <Check className="h-4 w-4" />
          Resolve
        </Button>
      ) : null}
    </div>
  );
}
